import './MessengerView.scss';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import zIndex from 'fe-design-base/styles/zIndex';
import { List } from 'immutable';
import PropTypes from 'prop-types';

import { createOneTimeEvent } from 'actions/session';
import { fetchLocationsData } from 'actions/settings';
import {
  actionTypes as teamEmailsActionTypes,
  fetchTeamAnnouncements,
} from 'actions/teamEmails';

import { hasUnreadApplicationConversations } from 'selectors/applicantMessenger';
import { buildGetDrawerOpen } from 'selectors/drawers';
import { getIsFetching } from 'selectors/fetching';
import {
  getCurrentUserIsManagerial,
  getMessagingEnabled,
  getRepackagingRollout,
  getRolePermission,
  getShouldShowSimplifiedLeftNav,
  getShowAnnouncementsV2,
  getShowCovidReactivationMessengerScrim,
} from 'selectors/session';

import { MSG_DRAWER_WIDTH } from 'features/messenger/constants';
import { toggleMessengerShown } from 'features/messengerv2/actions';
import {
  selectCurrentTab,
  selectHasChannels,
  selectIsFetchingChannels,
  selectShouldHideMessageDrawerTabs,
} from 'features/messengerv2/selectors';
import { setCurrentTab } from 'features/messengerv2/slice';
import ScrimGeneralModal from 'features/modals/ScrimGeneralModal';
import LeftNavMessengerDrawer from 'features/nav/LeftNav/MessengerDrawer';

import Drawer, { NAV_HEIGHT } from 'components/Drawer';
import Loader from 'components/Loader';
import ScrimWrapper from 'components/ScrimWrapper';

import { cxHelpers } from 'util/className';

import { MESSENGER_VIEW_DRAWER_KEY } from '../../drawers/keys';
import { MESSAGE_DRAWER_TABS } from '../constants';

import MessengerDrawer from './components/MessengerDrawer';
import { CONTAINER_STYLE, MESSENGER_SCRIM_EVENT_NAME } from './constants';

const { cxEl } = cxHelpers('MessengerView');

const MessengerView = ({
  onChangeTabView,
  onCreateOTE,
  onToggleMessenger,
  onFetchTeamAnnouncements,
  announcementsV2,
  onFetchLocationsData,
  isShown,
  messagingEnabled,
  showApplicantIndicator,
  isLoadingChannels,
  isLoadingAnnouncements,
  shouldHideTabs,
  currentTab,
  isManagerial,
  showMessengerScrim,
  repackagingEnabled,
  canViewHiring,
  hasChannels,
}) => {
  const [menuOpen, setMenuOpen] = useState(showMessengerScrim);
  const [initialLoad, setInitialLoad] = useState(!hasChannels);
  const shouldShowSimplifiedLeftNav = useSelector(
    getShouldShowSimplifiedLeftNav
  );

  useEffect(() => {
    if (messagingEnabled) {
      if (showMessengerScrim) onToggleMessenger(true);

      if (!isLoadingChannels && !hasChannels) {
        setInitialLoad(false);
      }

      onFetchLocationsData();
      if (announcementsV2) onFetchTeamAnnouncements();
      onChangeTabView(MESSAGE_DRAWER_TABS.TEAM);
    } else {
      onChangeTabView(
        canViewHiring
          ? MESSAGE_DRAWER_TABS.APPLICANTS
          : MESSAGE_DRAWER_TABS.ANNOUNCEMENTS
      );
      setInitialLoad(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagingEnabled]);

  const showIndicatorForTab = useCallback(
    name => {
      switch (name) {
        case 'team':
          return false;
        case 'applicants':
          return showApplicantIndicator;
        default:
          return false;
      }
    },
    [showApplicantIndicator]
  );

  const buildTab = useCallback(
    name => ({
      label: (
        <Text i18n={`message_drawer.tabs.${name}`} variant="bodySmBoldTight" />
      ),
      className: cxEl('tab'),
      value: MESSAGE_DRAWER_TABS[name.toUpperCase()],
      indicator: showIndicatorForTab(name),
    }),
    [showIndicatorForTab]
  );

  const tabs = useMemo(() => {
    const result = [];

    if (messagingEnabled) result.push(buildTab(MESSAGE_DRAWER_TABS.TEAM));

    if (canViewHiring) result.push(buildTab(MESSAGE_DRAWER_TABS.APPLICANTS));

    if (isManagerial || announcementsV2)
      result.push(buildTab(MESSAGE_DRAWER_TABS.ANNOUNCEMENTS));

    return List(result);
  }, [
    announcementsV2,
    buildTab,
    canViewHiring,
    isManagerial,
    messagingEnabled,
  ]);

  const toggleMenuOpen = useCallback(() => setMenuOpen(value => !value), []);

  const handleMenuClose = useCallback(() => setMenuOpen(false), []);

  const handleTabChange = useCallback(
    view => onChangeTabView(view),
    [onChangeTabView]
  );

  const handleClose = useCallback(
    () => onToggleMessenger(false),
    [onToggleMessenger]
  );

  const handleCloseMessengerScrim = useCallback(() => {
    if (showMessengerScrim) {
      onCreateOTE(MESSENGER_SCRIM_EVENT_NAME, 'Company');
    }
  }, [onCreateOTE, showMessengerScrim]);

  const isLoading = initialLoad || isLoadingAnnouncements;

  if (shouldShowSimplifiedLeftNav) {
    return (
      <LeftNavMessengerDrawer
        shouldHideTabs={shouldHideTabs}
        currentTab={currentTab}
        handleClose={handleClose}
        handleTabChange={handleTabChange}
        tabs={tabs}
        isShown={isShown}
        isLoading={isLoading}
        repackagingEnabled={repackagingEnabled}
        showMessengerScrim={showMessengerScrim}
        menuOpen={menuOpen}
        handleMenuClose={handleMenuClose}
        toggleMenuOpen={toggleMenuOpen}
        showMenu={isManagerial}
      />
    );
  }

  return (
    <ScrimWrapper
      isOpen={showMessengerScrim}
      arrowFlipY
      arrowTop={50}
      arrowRight={360}
      arrowRotate={30}
      arrowType="slightlyCurved"
      closeCallback={handleCloseMessengerScrim}
      content={
        <Box w={`calc(100vw - ${MSG_DRAWER_WIDTH}px)`} mb={100} hcenter>
          <ScrimGeneralModal
            i18nPrefix="message_drawer.scrim"
            onRequestClose={handleCloseMessengerScrim}
          />
        </Box>
      }
    >
      <Drawer
        width={MSG_DRAWER_WIDTH}
        open={isShown}
        top={NAV_HEIGHT}
        containerStyle={CONTAINER_STYLE}
        zIndex={zIndex.zindexBottomDrawer}
      >
        <Loader
          isLoading={isLoading}
          className={cxEl(isLoading ? 'loading' : 'loaded')}
        >
          <MessengerDrawer
            shouldHideTabs={shouldHideTabs}
            currentTab={currentTab}
            handleClose={handleClose}
            handleTabChange={handleTabChange}
            tabs={tabs}
            repackagingEnabled={repackagingEnabled}
            showMessengerScrim={showMessengerScrim}
            menuOpen={menuOpen}
            handleMenuClose={handleMenuClose}
            toggleMenuOpen={toggleMenuOpen}
            showMenu={isManagerial}
          />
        </Loader>
      </Drawer>
    </ScrimWrapper>
  );
};

MessengerView.propTypes = {
  onChangeTabView: PropTypes.func.isRequired,
  onCreateOTE: PropTypes.func.isRequired,
  onToggleMessenger: PropTypes.func.isRequired,
  onFetchTeamAnnouncements: PropTypes.func.isRequired,
  announcementsV2: PropTypes.bool.isRequired,
  isShown: PropTypes.bool.isRequired,
  messagingEnabled: PropTypes.bool,
  hasChannels: PropTypes.bool,
  showApplicantIndicator: PropTypes.bool.isRequired,
  isLoadingChannels: PropTypes.bool,
  isLoadingAnnouncements: PropTypes.bool.isRequired,
  shouldHideTabs: PropTypes.bool.isRequired,
  currentTab: PropTypes.string.isRequired,
  isManagerial: PropTypes.bool,
  showMessengerScrim: PropTypes.bool,
  repackagingEnabled: PropTypes.bool.isRequired,
};

export default connect(
  (state, props) => ({
    hasChannels: selectHasChannels(state),
    currentTab: selectCurrentTab(state),
    messagingEnabled: getMessagingEnabled(state),
    announcementsV2: getShowAnnouncementsV2(state),
    repackagingEnabled: getRepackagingRollout(state),
    isLoadingChannels: selectIsFetchingChannels(state),
    shouldHideTabs: selectShouldHideMessageDrawerTabs(state),
    isManagerial: getCurrentUserIsManagerial(state, props),
    showApplicantIndicator: hasUnreadApplicationConversations(state),
    showMessengerScrim: getShowCovidReactivationMessengerScrim(state),
    isLoadingAnnouncements: getIsFetching(state, {
      requestId: teamEmailsActionTypes.FETCH_TEAM_ANNOUNCEMENTS_REQUEST,
    }),
    canViewHiring: getRolePermission(state, { name: 'manage_hiring' }),
    isShown: buildGetDrawerOpen(MESSENGER_VIEW_DRAWER_KEY)(state),
  }),
  {
    onFetchLocationsData: fetchLocationsData,
    onChangeTabView: setCurrentTab,
    onCreateOTE: createOneTimeEvent,
    onToggleMessenger: toggleMessengerShown,
    onFetchTeamAnnouncements: fetchTeamAnnouncements,
  }
)(memo(MessengerView));
